import { z } from 'zod';
import { ROLES } from '../constants/rbac';
import { uuidSchema } from './common';
import type { SecuritySettings } from './security';

/**
 * Публичной регистрации нет: это база персональных данных,
 * аккаунты заводит только администратор (раздел 7 ТЗ).
 */

/**
 * Вход по логину ИЛИ по e-mail — одним полем.
 * Основной аккаунт заказчика — логин `yana`.
 */
export const loginSchema = z.object({
  login: z.string().trim().min(2, 'Укажите логин или e-mail').max(255),
  // Пустое поле — это «не ввели», а не «слишком короткий»: на пустой форме
  // «Пароль короче 8 символов» читается как упрёк ни за что
  password: z.string().min(1, 'Укажите пароль').min(8, 'Пароль короче 8 символов').max(200),
  rememberMe: z.boolean().default(true),
  browserProof: z.object({
    challengeId: z.string().uuid(),
    publicKey: z.object({
      kty: z.literal('EC'), crv: z.literal('P-256'),
      x: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
      y: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    }),
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
  }).optional(),
});
export type LoginPayload = z.infer<typeof loginSchema>;

export const browserKeyIdSchema = z.string().trim().regex(/^[A-Za-z0-9_-]{43}$/, 'Скопируйте полный код браузера с экрана входа');
export const browserChallengeSchema = z.object({
  login: z.string().trim().min(2).max(255), keyId: browserKeyIdSchema,
});
export const browserAccessSchema = z.object({
  keyId: browserKeyIdSchema,
  label: z.string().trim().min(1, 'Укажите название браузера').max(120),
  password: z.string().min(8, 'Пароль короче 8 символов').max(200),
});
export interface BrowserAccessItem {
  id: string; keyId: string; label: string; createdAt: string;
  revokedAt: string | null; lastLoginAt: string | null;
}

/**
 * Разрешённые символы логина — одно правило на всю систему.
 *
 * У better-auth своя встроенная проверка, и она строже: дефис в ней
 * не предусмотрен. Пока правила расходились, сотрудник с логином вида
 * `anna-k` заводился без единой жалобы, а войти по логину не мог никогда —
 * только по почте. Поэтому плагину передаётся именно эта проверка
 * (см. apps/api/src/auth/index.ts), а не его собственная.
 */
export const USERNAME_PATTERN = /^[a-z0-9._-]+$/;
export const USERNAME_MIN = 2;
export const USERNAME_MAX = 32;

/** Годится ли строка в логин. Используется и схемой, и better-auth. */
export function isValidUsername(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v.length >= USERNAME_MIN && v.length <= USERNAME_MAX && USERNAME_PATTERN.test(v);
}

export const usernameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(USERNAME_MIN, 'Логин короче 2 символов')
  .max(USERNAME_MAX)
  .regex(USERNAME_PATTERN, 'Только латиница, цифры, точка, дефис и подчёркивание');

export const createUserSchema = z.object({
  username: usernameSchema,
  email: z.string().trim().toLowerCase().email('Некорректный e-mail'),
  name: z.string().trim().min(2, 'Укажите имя').max(120),
  role: z.enum(ROLES),
  password: z.string().min(8, 'Пароль короче 8 символов').max(200),
  /** Потребовать смену пароля при первом входе */
  mustChangePassword: z.boolean().default(true),
});

export const updateUserSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  email: z.string().trim().toLowerCase().email().optional(),
  role: z.enum(ROLES).optional(),
  isActive: z.boolean().optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  reassignToUserId: z.string().min(1).max(128).optional(),
}).refine((value) => value.reassignToUserId === undefined || (
  value.isActive === false && Object.keys(value).every((key) => ['isActive', 'reassignToUserId'].includes(key))
), { message: 'Передача работы выполняется вместе с блокировкой', path: ['reassignToUserId'] });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Введите текущий пароль'),
    newPassword: z.string().min(8, 'Пароль должен быть не короче 8 символов').max(200),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  });

export const sessionUserSchema = z.object({
  id: uuidSchema,
  username: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.enum(ROLES),
  /** Цвет пользователя: курсор в гриде, аватар в списке присутствия */
  color: z.string(),
  initials: z.string(),
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  permissions: z.array(z.string()),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * Ответ `/session/me`: пользователь плюс общие настройки.
 *
 * Настройки едут вместе с сессией, а не отдельным запросом, чтобы предел
 * копирования был известен до первого Ctrl+C, а не приезжал через секунду
 * после того, как человек уже нажал.
 *
 * В сам `SessionUser` они не входят намеренно: его собирает middleware
 * на КАЖДЫЙ запрос, и лишняя выборка из `app_setting` там ни к чему.
 */
export interface SessionResponse extends SessionUser {
  security: SecuritySettings;
}

export const userListItemSchema = sessionUserSchema.extend({
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
